import axios from "axios";
import { useCallback, useMemo } from "react";
import {
  PrivacyProvider,
  PrivacySignature,
  proofDataToString,
  Provider,
} from "rujira.ui";
import { useWalletAccounts } from "../services/accounts";
import { useRedactedProof } from ".";
import { deriveStealth } from "./clientProof";
import { REDACTED_BACKEND_URL, REDACTED_PRIVACY_CHAIN_ID } from "./config";
import { useRedactedV2 } from "./v2";

/**
 * The app-side implementation of rujira.ui's PrivacyProvider: encapsulates
 * the Redacted backend (URL + HTTP requests), proof generation and the
 * environment-specific chain ID for wallet message signing, so the shared
 * UI package stays free of privacy-specific logic.
 */
export const usePrivacyProvider = (): PrivacyProvider => {
  const { selected } = useWalletAccounts();
  const { generateProofWithParams, getNonce } = useRedactedProof(
    selected?.provider
  );
  const v2 = useRedactedV2();

  const signMessage = useCallback(
    async (
      address: string,
      message: string
    ): Promise<PrivacySignature | null> => {
      try {
        if (!selected) return null;
        const signer = Provider.signer(selected.provider);
        // This bridge authenticates against the legacy ADR-36-only backend.
        // EVM message signing belongs to the current private-account session.
        if (signer.messageSigningScheme === "eip191") return null;
        if (!signer.signMessage) {
          console.error("---No wallet for signature");
          return null;
        }
        const signature = await signer.signMessage(
          REDACTED_PRIVACY_CHAIN_ID,
          address,
          message
        );
        return signature || null;
      } catch {
        // cancelled signature
        return null;
      }
    },
    [selected]
  );

  return useMemo<PrivacyProvider>(
    () => ({
      version: v2.enabled ? 2 : 1,
      v2: v2.enabled
        ? {
            status: v2.status,
            error: v2.error,
            sessionExpired: v2.sessionExpired,
            privateSessionLocked: v2.privateSessionLocked,
            sessionPolicy: v2.sessionPolicy,
            sessionPolicySaved: v2.sessionPolicySaved,
            updateSessionPolicy: v2.updateSessionPolicy,
            dismissSessionExpired: v2.dismissSessionExpired,
            requestSessionUnlock: v2.requestSessionUnlock,
            progress: v2.progress,
            subwallets: v2.subwallets,
            selectedSubwallet: v2.selectedSubwallet,
            accounts: v2.accounts,
            activeAccountId: v2.activeAccountId,
            selectAccount: v2.selectAccount,
            renameAccount: v2.renameAccount,
            removeAccount: v2.removeAccount,
            recoveryExportRequired: v2.recoveryExportRequired,
            activateWithWallet: v2.activateWithWallet,
            continueWalletUnlock: v2.continueWalletUnlock,
            cancelWalletUnlock: v2.cancelWalletUnlock,
            createRandomRecovery: v2.createRandomRecovery,
            confirmRecoveryExport: v2.confirmRecoveryExport,
            importRecovery: v2.importRecovery,
            exportRecovery: v2.exportRecovery,
            onboardCurrent: v2.onboardCurrent,
            selectSubwallet: v2.selectSubwallet,
            showPublic: v2.showPublic,
            showPrivate: v2.showPrivate,
            lock: v2.lock,
          }
        : undefined,
      signMessage,
      getNonce,
      generateProof: generateProofWithParams,
      isRegisteredPrivateAccount: async (address) => {
        try {
          const res = await axios.post(
            `${REDACTED_BACKEND_URL}/isRegisteredPrivateAccount`,
            {
              address,
            }
          );
          return res.data?.ok ? res.data : null;
        } catch (error) {
          return null;
        }
      },
      getStealth: async (address, signature) => {
        try {
          const res = await axios.post(`${REDACTED_BACKEND_URL}/getStealth`, {
            address: address,
            pubkey: signature.pub_key.value,
            signature: signature.signature,
          });
          return res.data?.ok ? res.data : null;
        } catch (error) {
          return null;
        }
      },
      register: async ({ address, derivationSignature }) => {
        try {
          const stealth = await deriveStealth(address, derivationSignature);
          const signature = await signMessage(
            address,
            `${address.toLowerCase()}-${stealth.toLowerCase()}`
          );
          if (!signature) {
            return { ok: false, err: "Registration signature cancelled" };
          }
          const res = await axios.post(`${REDACTED_BACKEND_URL}/register`, {
            pubkey: signature.pub_key.value,
            address,
            stealth,
            signature: signature.signature,
          });
          return res.data.ok
            ? { ok: true, stealth: res.data.stealth }
            : { ok: false, err: res.data.err };
        } catch (error: any) {
          return { ok: false, err: error?.message };
        }
      },
      createSubWallet: async ({
        address,
        stealth,
        param,
        proof,
        signature,
      }) => {
        try {
          const res = await axios.post(
            `${REDACTED_BACKEND_URL}/createSubWallet`,
            {
              proof: proofDataToString(proof),
              param,
              address,
              stealth,
              signature: signature.signature,
              pubkey: signature.pub_key.value,
            }
          );
          return res.data && res.data.ok ? res.data.subWallet : null;
        } catch (error) {
          return null;
        }
      },
    }),
    [signMessage, getNonce, generateProofWithParams, v2]
  );
};
